import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangleIcon, CheckCircle2Icon, InfoIcon, XCircleIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        info: 'border-info/25 bg-info/6 text-foreground [&>svg]:text-info',
        success: 'border-success/25 bg-success/6 text-foreground [&>svg]:text-success',
        warning: 'border-warning/30 bg-warning/8 text-foreground [&>svg]:text-warning',
        destructive:
          'border-destructive/30 bg-destructive/6 text-foreground [&>svg]:text-destructive',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-[13px] text-muted-foreground [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

const ALERT_ICONS = {
  default: InfoIcon,
  info: InfoIcon,
  success: CheckCircle2Icon,
  warning: AlertTriangleIcon,
  destructive: XCircleIcon,
} as const;

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>['variant']>;

/**
 * The common shape in one element: icon, title, optional description and an
 * optional action on the trailing edge. Equivalent of antd's
 * `<Alert type showIcon message description action />`.
 */
function Callout({
  variant = 'info',
  title,
  description,
  action,
  icon,
  className,
}: {
  variant?: AlertVariant;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  const Icon = ALERT_ICONS[variant];

  return (
    <Alert variant={variant} className={className}>
      {icon ?? <Icon />}
      <div className="col-start-2 flex items-start justify-between gap-3">
        <div className="grid min-w-0 gap-0.5">
          {title ? <div className="font-medium">{title}</div> : null}
          {description ? (
            <div className="text-[13px] text-muted-foreground">{description}</div>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </Alert>
  );
}

export { Alert, AlertTitle, AlertDescription, Callout };
